import React, { FC, useEffect, useState } from 'react';
import './Batches.scss';
import { useTranslation } from 'react-i18next';
import { exportCSVShelvedPayouts } from '../../store/order/actions/exports';
import { Table } from '../../components/table/Table';
import { TablesNames } from '../../store/tables/tables-context';
import { BUTTON_TYPE, ButtonComponent } from '../../components/ButtonComponent';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../../components/modal/ModalWindow';
import { batchAction } from '../../store/order/actions/batches';
import { getFormattedBatchesData } from '../../interfaces/funds';
import { fetchShelvedBatchesData, setBatchesDataToDefault } from '../../store/payments/actions/batches';
import { IBatchesFiltersParams } from '../../components/batches/BatchesFilterSection';
import { useLocation } from 'react-router';
import { parseSearchParams } from '../../utils/utilities';
import { usePaymentContext } from '../../store/payments/payments-context';
import { BATCH_ACTIONS } from '../../components/batches/BatchDetailsInformationHeader';
import { EmptyBatches } from '../../components/batches/EmptyBatches';
import { ExportCSVButton } from '../../components/form/ExportCSVButton';

interface Props {
  listener?: (search: any) => Promise<void>;
}

export const ShelvedBatchesList: FC<Props> = ({ listener }) => {
    const { t } = useTranslation();

    const { search } = useLocation();

    const { paymentContextState, dispatch } = usePaymentContext();
    const { shelvedBatchesData, isShelvedBatchesDataLoading } = paymentContextState;

    const [modalState, setModalState] = useState<IModalWindow>({
        isOpen: false
    });
    const [unshelveModalState, setUnshelveModalState] = useState({
        isOpen: false,
        batchId: '',
    });

    const filtersFromUrl: IBatchesFiltersParams = parseSearchParams(search) || {};

    const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    return () => {
      setBatchesDataToDefault(dispatch);
    };
  }, []);

    const closeUnshelveModal = (): void => setUnshelveModalState({ isOpen: false, batchId: '' });

    const successModalAction = async (): Promise<void> => {
        closeModal();
        await fetchShelvedBatchesData({ dispatch, params: filtersFromUrl });
    };

    const handleUnshelveSuccess = (): void => {
        closeUnshelveModal();
        setModalState({
            isOpen: true,
            modalType: MODAL_TYPE.APPROVE,
            modalTitle: t('PENDING_PAYOUTS.UNSHELVE_MODAL_TITLE'),
            modalText: 'The batch has been successfully moved to the Unposted batches list.',
            onClose: successModalAction,
            confirmButtonText: 'Ok',
            onConfirm: successModalAction,
        });
    };

    const rowActions = (rowData): JSX.Element => {
        return (
            <ButtonComponent
                buttonType={BUTTON_TYPE.WHITE}
                buttonName={t('PENDING_PAYOUTS.UNSHELVE_BTN_NAME')}
                isDisabled={!rowData?.original?.canPost}
                additionalClass="no-letter-spacing fs-7 py-1 ps-2 pe-3 d-block mx-auto"
                onButtonClick={(): void => setUnshelveModalState({
                    isOpen: true,
                    batchId: rowData?.original?.payoutId,
                })}
            />
        );
    };

    if (!search) {
      return <EmptyBatches />;
    }

    return (
      <>
          <div className="mb-10">
              <Table
                  name={TablesNames.unpostedBatchesTableData}
                  data={getFormattedBatchesData(shelvedBatchesData) ?? []}
                  isLoading={isShelvedBatchesDataLoading}
                  enableRowActions={true}
                  rowActions={rowActions}
                  actionColumnsSize={80}
                  queryParamsListener={listener}
                  toolbarCustomAction={
                      <>
                          {/*<Title title='Shelved payouts' additionalClass='fs-2 ms-2'/>*/}
                          <ExportCSVButton
                              orderTotalItemCount={shelvedBatchesData?.length ?? 0}
                              isExportCSVLoading={false} // TODO: add loading
                              exportCSV={async (): Promise<void> => await exportCSVShelvedPayouts()}
                          />
                      </>
                  }
              />
          </div>

          <ModalWindow
            {...modalState}
          />

          <ModalWindow
            isOpen={unshelveModalState.isOpen}
            modalType={MODAL_TYPE.SAVE}
            modalTitle={t('PENDING_PAYOUTS.UNSHELVE_MODAL_TITLE')}
            modalText={t('PENDING_PAYOUTS.UNSHELVE_MODAL_TEXT')}
            onCancel={closeUnshelveModal}
            onClose={closeUnshelveModal}
            onConfirm={(): Promise<void> => batchAction({
              batchId: unshelveModalState.batchId,
              action: BATCH_ACTIONS.UNSHELVE,
              onSuccess: handleUnshelveSuccess,
              onClose: closeUnshelveModal,
            })
            }
          />
      </>
    );
};
